import { useCallback, useRef } from 'react';

const ABORTABLE_DISPATCH_DELAY_MS = 150;

/**
 * Clicking a table button first blurs the focused cell editor, which can start a forced save whose
 * re-render unmounts or covers the button before the `click` event fires — losing the click entirely.
 * This arms a deferred fallback on `mousedown`: the blur handler (and its save registration) already
 * ran during mousedown's default action, so the action still executes after the save handling begins.
 *
 * The fallback only fires for the primary button, is delayed just long enough that an aborted press
 * (pointer leaves the element, is cancelled, or starts a drag) can be cancelled via `cancelDeferred`,
 * and the real click is swallowed once when the deferred dispatch already ran — a flag, not a time
 * window, so even a long press released on the button can't double-fire.
 */
export const useDeferredAction = () => {
  const deferredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swallowClickRef = useRef(false);

  const cancelDeferred = useCallback(() => {
    if (deferredTimerRef.current !== null) {
      clearTimeout(deferredTimerRef.current);
      deferredTimerRef.current = null;
    }
  }, []);

  const onMouseDown = useCallback(
    (event: React.MouseEvent, action: () => void) => {
      // Only the primary button produces a click — right/middle presses must not fire the action.
      if (event.button !== 0) return;

      cancelDeferred();
      swallowClickRef.current = false;
      deferredTimerRef.current = setTimeout(() => {
        deferredTimerRef.current = null;
        swallowClickRef.current = true;
        action();
      }, ABORTABLE_DISPATCH_DELAY_MS);
    },
    [cancelDeferred]
  );

  const onClick = useCallback((event: React.MouseEvent, action: () => void) => {
    event.stopPropagation();

    if (deferredTimerRef.current !== null) {
      // A real click arrived before the fallback — run the action now, don't wait.
      clearTimeout(deferredTimerRef.current);
      deferredTimerRef.current = null;
      action();
      return;
    }

    // A real click whose deferred mousedown action already ran must not fire it again.
    // Keyboard/programmatic clicks (detail === 0) always run — a leftover flag from a lost
    // click must not eat them.
    if (event.detail !== 0 && swallowClickRef.current) {
      swallowClickRef.current = false;
      return;
    }

    action();
  }, []);

  return { onMouseDown, onClick, cancelDeferred };
};
